import { message } from 'antd';
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getAccessToken } from '@/lib';
import { refreshToken } from './auth';

// 保存每个请求对应的 AbortController
const pendingMap = new Map();

// 存储key
function getRequestKey(config: InternalAxiosRequestConfig) {
  return `${config.method || 'get'}_${config.url}`;
}

// 删除key
function clearPending(config: InternalAxiosRequestConfig) {
  const key = getRequestKey(config);
  pendingMap.delete(key);
}

export const apiClient = axios.create({
  baseURL: '/api/main',
  timeout: 20000,
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    config.retry ??= 2;
    config.retryDelay ??= 1000;

    const key = getRequestKey(config);
    const token = getAccessToken();

    // 无 token → 阻止本次请求
    if (!token) {
      return Promise.reject({
        code: 401,
        message: '认证失败，请重新登录',
      });
    }

    // 已有同类型请求 → 取消掉
    if (pendingMap.has(key)) {
      const controller = pendingMap.get(key);
      controller.abort(); // 执行取消
      pendingMap.delete(key);
    }

    // 创建新的 controller
    const controller = new AbortController();
    config.signal = controller.signal;

    // 保存这次的 controller
    pendingMap.set(key, controller);

    config.headers.Authorization = `Bearer ${token}`;
    // 2. 自定义公共 header
    config.headers['x-client-type'] = 'web'; // 用于追踪
    config.headers['x-request-id'] = Date.now() + ''; // 用于追踪

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    clearPending(response.config);
    return handleSuccess(response);
  },
  async (error) => {
    // 处理重试之前先把 pending 清掉
    if (error?.config) clearPending(error.config);
    return handleError(error);
  },
);

// 成功
function handleSuccess(response: AxiosResponse<ApiResult<any>>) {
  const res = response.data;

  if (res.code === 200) {
    return response;
  } else {
    message.error(res.message || '请求出错');
    // 业务异常（但网络没问题）
    Promise.reject({
      type: 'BUSINESS_ERROR',
      message: res.message || '业务错误',
      response: response,
    });
    return response;
  }
}

type ApiError = AxiosError<ApiResult<any>>;
// 失败
async function handleError(error: ApiError) {
  try {
    // 先尝试重试（可能 resolve 重试）
    return await handleRetry(error);
  } catch (e) {
    // 重试失败 → 走正常错误处理
    return handleHttpError(error);
  }
}

// 重试
async function handleRetry(error: ApiError) {
  const config = error.config;

  // 请求本身不存在
  if (!config) return Promise.reject(error);

  // 配置默认重试次数
  config.retry = config.retry ?? apiClient.defaults.retry ?? 2;
  config.retryDelay =
    config.retryDelay ?? apiClient.defaults.retryDelay ?? 1000;

  config.__retryCount = config.__retryCount || 0;

  // 超出最大重试次数
  if (config.__retryCount >= config.retry) {
    return Promise.reject(error);
  }

  const status = error.response?.status ?? 0;
  // 可重试条件
  const shouldRetry =
    error.code === 'ECONNABORTED' || // 客户端超时
    error.code === 'ERR_NETWORK' || // 网络断开 / CORS
    error.response?.status === 408 || // 服务端超时
    (status >= 500 && status < 600);

  // 不可重试（逻辑错误）
  const cannotRetry = [400, 401, 403, 404, 422, 500].includes(status);

  if (!shouldRetry || cannotRetry) {
    return Promise.reject(error);
  }

  // 重试！
  config.__retryCount += 1;

  const delay = config.retryDelay * config.__retryCount;
  await new Promise((res) => setTimeout(res, delay));

  console.log(`🔄 Retry ${config.__retryCount}/${config.retry}: ${config.url}`);

  return apiClient(config);
}

// 刷新 Token 逻辑
let isRefreshing = false; //必须放外面
let pendingQueue: Array<(newToken: string) => void> = []; //多个请求同时401
const handleRefreshToken = async (error: ApiError) => {
  // 如果没有 refreshToken，没法刷新，直接登出
  // if (!getStorageItem('refreshToken')) {
  //     sessionStorage.clear();
  //     // 这里可以跳登录页
  //     // window.location.href = '/login';
  //     return Promise.reject(error);
  // }

  const config = error.config;
  if (!config) return Promise.reject(error);

  // 已经在刷新中：把当前请求挂到队列，等刷新完继续发
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingQueue.push((newToken) => {
        // 修改原请求里的 token，再重发
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        apiClient(config).then(resolve).catch(reject);
      });
    });
  }

  // 没在刷新：开始刷新
  isRefreshing = true;

  try {
    const newToken = await refreshToken();

    // 把队列里的请求都放行
    pendingQueue.forEach((cb) => {
      cb(newToken);
    });
    pendingQueue = [];
    isRefreshing = false;

    // 重发当前这一个请求
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${newToken}`;
    return apiClient(config);
  } catch (e) {
    isRefreshing = false;
    pendingQueue = [];
    sessionStorage.clear();
    // 刷新失败 → 统一处理，如跳登录
    // window.location.href = '/login';
    return Promise.reject(e);
  }
};

// 处理错误
function handleHttpError(error: ApiError) {
  const { response, request } = error;

  if (response) {
    return handleResponseError(error);
  } else if (request) {
    return handleRequestError(error);
  } else {
    // 请求未发出就出错（极少）
    return Promise.reject({
      type: 'REQUEST_CONFIG_ERROR',
      message: error.message,
      raw: error,
    });
  }
}

// 接口返回错误
function handleResponseError(error: ApiError) {
  const status = error.response?.status ?? 0;

  let msg = '';
  switch (status) {
    case 400:
      msg = '请求参数错误';
      break;
    case 401: {
      handleRefreshToken(error);
      msg = '未授权或 Token 失效';
      break;
    }
    case 403:
      msg = '禁止访问';
      break;
    case 404:
      msg = '资源不存在';
      break;
    case 500:
      msg = '服务器错误';
      break;
    default:
      msg = `连接错误：${status}`;
  }

  return Promise.reject({
    type: 'HTTP_ERROR',
    message: msg,
    status,
    error: error,
  });
}

// 接口请求错误
function handleRequestError(error: ApiError) {
  const { code } = error;

  let msg = '';
  switch (code) {
    case 'ERR_CANCELED':
      msg = '请求已取消';
      break;
    case 'ECONNABORTED':
      msg = '请求超时';
      break;
    case 'ERR_NETWORK':
      msg = '网络错误';
      break;
    case 'ERR_BAD_REQUEST':
      msg = '客户端请求出错';
      break;
    case 'ERR_BAD_RESPONSE':
      msg = '服务端端请求出错';
      break;
    default:
      msg = '无法连接服务器（CORS / 断网 / 服务未启动）';
  }

  // 请求已发出 → 未收到响应
  return Promise.reject({
    type: 'NETWORK_ERROR',
    message: msg,
    error: error,
  });
}

export type ApiResult<T = any> = {
  code: number;
  data: T;
  message?: string;
  msg?: string;
};

// ① 主函数：request<T>(url, config)
function coreRequest<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResult<T>> {
  return apiClient
    .request<any, AxiosResponse<ApiResult<T>>>({
      ...(config || {}),
      url,
    })
    .then((r) => r.data);
}

// ② 给函数挂链式方法（get/post/put/delete/patch）
type RequestFn = (<T>(
  url: string,
  config?: AxiosRequestConfig,
) => Promise<ApiResult<T>>) & {
  get: <T>(url: string, config?: AxiosRequestConfig) => Promise<ApiResult<T>>;
  delete: <T>(
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResult<T>>;
  post: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResult<T>>;
  put: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResult<T>>;
  patch: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) => Promise<ApiResult<T>>;
};

export const request = coreRequest as RequestFn;

request.get = <T>(url: string, config?: AxiosRequestConfig) =>
  coreRequest<T>(url, { ...(config || {}), method: 'GET' });

request.delete = <T>(url: string, config?: AxiosRequestConfig) =>
  coreRequest<T>(url, { ...(config || {}), method: 'DELETE' });

request.post = <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
  coreRequest<T>(url, { ...(config || {}), method: 'POST', data });

request.put = <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
  coreRequest<T>(url, { ...(config || {}), method: 'PUT', data });

request.patch = <T>(url: string, data?: any, config?: AxiosRequestConfig) =>
  coreRequest<T>(url, { ...(config || {}), method: 'PATCH', data });

export default request;
